/**
 * Purge des POI automatiques — EF-POI-016. Le nombre de POI concernés par
 * chaque critère est affiché avant confirmation, et la confirmation est
 * explicite.
 */

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { SHEETS } from '../labels';
import { Sheet } from './Sheet';
import { closeSheet, purgeCandidates, purgeCounts, runPurge, type PurgeCriteria } from '../state';

export function PurgeSheet(): JSX.Element {
  const [criteria, setCriteria] = useState<PurgeCriteria>({
    absentOrRejected: true,
    outOfCorridor: false,
    outOfPortion: false,
    disabledCategory: false,
  });

  const counts = purgeCounts();
  const total = purgeCandidates(criteria).length;

  const toggle = (key: keyof PurgeCriteria) => (): void => {
    setCriteria((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <Sheet title={SHEETS.purge} onClose={closeSheet}>
      <p>
        Un POI manuel, épinglé, annoté ou exclu n’est jamais supprimé. Un POI dont la zone n’a pas été
        recherchée n’est jamais proposé.
      </p>

      <fieldset>
        <legend>Critères</legend>
        <Criterion
          label="Absent du dernier résultat ou écarté par un filtre"
          count={counts.absentOrRejected}
          checked={criteria.absentOrRejected}
          onToggle={toggle('absentOrRejected')}
        />
        <Criterion
          label="Aucun passage dans le corridor courant"
          count={counts.outOfCorridor}
          checked={criteria.outOfCorridor}
          onToggle={toggle('outOfCorridor')}
        />
        <Criterion
          label="Hors portion recherchée"
          count={counts.outOfPortion}
          checked={criteria.outOfPortion}
          onToggle={toggle('outOfPortion')}
        />
        <Criterion
          label="Aucune catégorie activée dans les paramètres courants"
          count={counts.disabledCategory}
          checked={criteria.disabledCategory}
          onToggle={toggle('disabledCategory')}
        />
      </fieldset>

      <p>
        <strong>{total}</strong> POI seront supprimés. La suppression reste annulable tant que vous ne
        quittez pas l’onglet Roadbook.
      </p>

      <div class="actions">
        <button type="button" onClick={closeSheet}>
          Annuler
        </button>
        <button
          type="button"
          class="danger"
          disabled={total === 0}
          onClick={() => {
            void runPurge(criteria);
            closeSheet();
          }}
        >
          Supprimer {total} POI
        </button>
      </div>
    </Sheet>
  );
}

function Criterion(props: {
  readonly label: string;
  readonly count: number;
  readonly checked: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  return (
    <label class="checkbox">
      <input type="checkbox" checked={props.checked} onChange={props.onToggle} />
      <span>
        {props.label} — <strong>{props.count}</strong> POI
      </span>
    </label>
  );
}
